import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { apiClient } from '@/services/api'
import type { MatchView, UpdateMatchRequest, Series } from '@/types/api'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

interface EditMatchDialogProps {
  match: MatchView | null
  isOpen: boolean
  onClose: () => void
  onMatchUpdated: (updatedMatch: MatchView) => void
  onMatchDeleted?: (matchId: string) => void
  seriesStartDate?: string
  seriesEndDate?: string
  series?: Series
}

export function EditMatchDialog({
  match,
  isOpen,
  onClose,
  onMatchUpdated,
  onMatchDeleted,
  seriesStartDate,
  seriesEndDate,
  series,
}: EditMatchDialogProps) {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [scoreA, setScoreA] = useState<number>(0)
  const [scoreB, setScoreB] = useState<number>(0)
  const [playedAtDate, setPlayedAtDate] = useState<string>('')
  const [playedAtTime, setPlayedAtTime] = useState<string>('')
  const [outcome, setOutcome] = useState<'A' | 'B' | 'DRAW'>('A')
  
  const isOutcomeScoring = series?.scoringProfile === 'SCORING_PROFILE_MATCH_OUTCOME'
  const getSetsToPlay = () => series?.setsToPlay || 5
  const getRequiredWins = () => Math.ceil(getSetsToPlay() / 2)

  // Helper function to validate table tennis scores
  const validateTableTennisScore = (scoreA: number, scoreB: number) => {
    const setsToPlay = getSetsToPlay()
    const requiredWins = Math.ceil(setsToPlay / 2)

    // Generate valid results based on setsToPlay
    const validResults: [number, number][] = []
    
    // Winner gets requiredWins, loser gets 0 to requiredWins-1
    for (let loserSets = 0; loserSets < requiredWins; loserSets++) {
      validResults.push([requiredWins, loserSets])
      validResults.push([loserSets, requiredWins])
    }
    
    return validResults.some(([a, b]) => a === scoreA && b === scoreB)
  }

  // Helper to determine outcome from match
  const determineOutcome = (match: MatchView): 'A' | 'B' | 'DRAW' => {
    if (match.outcome === 'OUTCOME_PLAYER_A_WIN') return 'A'
    if (match.outcome === 'OUTCOME_PLAYER_B_WIN') return 'B'
    if (match.outcome === 'OUTCOME_DRAW') return 'DRAW'
    // Fallback for set-based
    if (match.scoreA > match.scoreB) return 'A'
    if (match.scoreB > match.scoreA) return 'B'
    return 'DRAW'
  }

  useEffect(() => {
    if (match) {
      setScoreA(match.scoreA)
      setScoreB(match.scoreB)
      setOutcome(determineOutcome(match))
      setShowDeleteConfirm(false)
      // Convert ISO date to input date format (YYYY-MM-DD)
      const matchDate = new Date(match.playedAt)
      setPlayedAtDate(matchDate.toISOString().split('T')[0])
      // Convert to HH:MM format
      setPlayedAtTime(matchDate.toTimeString().slice(0, 5))
    }
  }, [match])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!match) return

    // For outcome-based scoring, convert outcome to scores
    let finalScoreA = scoreA
    let finalScoreB = scoreB
    
    if (isOutcomeScoring) {
      // Chess: 1-0 for win, 0-1 for loss, 1-1 for draw
      if (outcome === 'A') {
        finalScoreA = 1
        finalScoreB = 0
      } else if (outcome === 'B') {
        finalScoreA = 0
        finalScoreB = 1
      } else {
        finalScoreA = 1
        finalScoreB = 1
      }
    } else {
      // Validate set-based scores
      if (!validateTableTennisScore(finalScoreA, finalScoreB)) {
        const requiredWins = getRequiredWins()
        const validResults: string[] = []
        for (let loserSets = 0; loserSets < requiredWins; loserSets++) {
          validResults.push(`${requiredWins}-${loserSets}`)
        }
        toast.error(`Invalid result. Valid results are: ${validResults.join(', ')}`)
        return
      }
    }

    // Validate match date is within series window if being updated (inclusive)
    if (seriesStartDate && seriesEndDate && playedAtDate !== new Date(match.playedAt).toISOString().split('T')[0]) {
      const matchDate = new Date(playedAtDate)
      const startDate = new Date(seriesStartDate)
      const endDate = new Date(seriesEndDate)
      
      // Set times to compare only dates (start of day for start, end of day for end)
      startDate.setHours(0, 0, 0, 0)
      endDate.setHours(23, 59, 59, 999)
      matchDate.setHours(12, 0, 0, 0) // Set to middle of day for comparison
      
      if (matchDate < startDate || matchDate > endDate) {
        toast.error(
          `${t('matches.validation.seriesWindow')} (${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()})`
        )
        return
      }
    }

    try {
      setLoading(true)
      
      // Combine date and time for comparison and API call
      const originalDate = new Date(match.playedAt)
      const newDateTime = `${playedAtDate}T${playedAtTime}:00`
      const hasDateTimeChanged = playedAtDate !== originalDate.toISOString().split('T')[0] || 
                                 playedAtTime !== originalDate.toTimeString().slice(0, 5)
      
      const updateRequest: UpdateMatchRequest = {
        matchId: match.id,
        scoreA: finalScoreA !== match.scoreA ? finalScoreA : undefined,
        scoreB: finalScoreB !== match.scoreB ? finalScoreB : undefined,
        playedAt: hasDateTimeChanged ? new Date(newDateTime).toISOString() : undefined,
      }

      const response = await apiClient.updateMatch(updateRequest)
      onMatchUpdated(response.match)
      toast.success(t('matches.updated'))
      onClose()
    } catch (error: unknown) {
      toast.error((error as Error).message || t('error.generic'))
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!match || !onMatchDeleted) return

    try {
      setDeleting(true)
      await apiClient.deleteMatch({ matchId: match.id })
      onMatchDeleted(match.id)
      toast.success(t('matches.deleted'))
      onClose()
    } catch (error: unknown) {
      toast.error((error as Error).message || t('error.generic'))
    } finally {
      setDeleting(false)
    }
  }

  if (!match) return null

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px] max-h-[90vh] p-0">
        <div className="p-6 pb-0">
          <DialogHeader>
            <DialogTitle>{t('matches.editTitle')}</DialogTitle>
            <DialogDescription>
              {match.playerAName} vs {match.playerBName}
            </DialogDescription>
          </DialogHeader>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <DialogBody className="px-6">
            <div className="grid gap-4">
              {isOutcomeScoring ? (
                <div className="space-y-3">
                  <Label>{t('matches.outcome.label')}</Label>
                  <RadioGroup value={outcome} onValueChange={(value) => setOutcome(value as 'A' | 'B' | 'DRAW')}>
                    <div className="flex items-center space-x-3 rounded-md border border-border bg-background p-3">
                      <RadioGroupItem value="A" id="outcome_a" />
                      <Label htmlFor="outcome_a" className="flex-1 cursor-pointer">
                        {match.playerAName} {t('common.wins', 'vinner')}
                      </Label>
                    </div>
                    <div className="flex items-center space-x-3 rounded-md border border-border bg-background p-3">
                      <RadioGroupItem value="B" id="outcome_b" />
                      <Label htmlFor="outcome_b" className="flex-1 cursor-pointer">
                        {match.playerBName} {t('common.wins', 'vinner')}
                      </Label>
                    </div>
                    <div className="flex items-center space-x-3 rounded-md border border-border bg-background p-3">
                      <RadioGroupItem value="DRAW" id="outcome_draw" />
                      <Label htmlFor="outcome_draw" className="flex-1 cursor-pointer">
                        {t('matches.outcome.draw')}
                      </Label>
                    </div>
                  </RadioGroup>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="scoreA" className="text-right">
                      {match.playerAName}
                    </Label>
                    <Input
                      id="scoreA"
                      type="number"
                      min="0"
                      max={getRequiredWins()}
                      value={scoreA}
                      onChange={(e) => setScoreA(parseInt(e.target.value, 10) || 0)}
                      className="col-span-3"
                    />
                  </div>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="scoreB" className="text-right">
                      {match.playerBName}
                    </Label>
                    <Input
                      id="scoreB"
                      type="number"
                      min="0"
                      max={getRequiredWins()}
                      value={scoreB}
                      onChange={(e) => setScoreB(parseInt(e.target.value, 10) || 0)}
                      className="col-span-3"
                    />
                  </div>
                  <div className="text-xs text-muted-foreground text-center">
                    {t('matches.validation.bestOfFormat', { 
                      sets: getSetsToPlay(), 
                      wins: getRequiredWins() 
                    })}
                  </div>
                </>
              )}
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="playedAtDate" className="text-right">
                  {t('matches.played_at_date')}
                </Label>
                <Input
                  id="playedAtDate"
                  type="date"
                  value={playedAtDate}
                  onChange={(e) => setPlayedAtDate(e.target.value)}
                  className="col-span-3"
                  required
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="playedAtTime" className="text-right">
                  {t('matches.played_at_time')}
                </Label>
                <Input
                  id="playedAtTime"
                  type="time"
                  value={playedAtTime}
                  onChange={(e) => setPlayedAtTime(e.target.value)}
                  className="col-span-3"
                  required
                />
              </div>
            </div>
          </DialogBody>
          <div className="px-6 pb-6">
            <DialogFooter>
              {!showDeleteConfirm ? (
                <>
                  {onMatchDeleted && (
                    <Button 
                      type="button" 
                      variant="destructive" 
                      onClick={() => setShowDeleteConfirm(true)}
                      className="mr-auto"
                    >
                      {t('common.delete')}
                    </Button>
                  )}
                  <Button type="button" variant="outline" onClick={onClose}>
                    {t('common.cancel')}
                  </Button>
                  <Button type="submit" disabled={loading}>
                    {loading ? t('common.loading') : t('common.save')}
                  </Button>
                </>
              ) : (
                <>
                  <div className="flex-1 text-sm text-destructive">
                    {t('matches.deleteConfirm')}
                  </div>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setShowDeleteConfirm(false)}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button 
                    type="button" 
                    variant="destructive" 
                    onClick={handleDelete}
                    disabled={deleting}
                  >
                    {deleting ? t('common.loading') : t('common.confirmDelete')}
                  </Button>
                </>
              )}
            </DialogFooter>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}